import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Layers } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { useWpwHandovers } from "../hooks";
import { SectionCard, Th } from "./ui";

/** Wejścia do masowej generacji pakietów WPW (ENERGA) — po jednym wierszu na
 *  zamówienie WPW projektu (wyprowadzone z przekazań; TanStack dedupluje query
 *  z innymi użyciami). Nic nie renderuje, dopóki projekt nie ma zamówień WPW.
 *  Osobna karta z nagłówkiem i tabelą (zamówienie · rodzaj · wykonawca · status ·
 *  zwroty z terenu), żeby sekcja nie ginęła między paskiem opcji a filtrami.
 *  `projectId` = id PRZEDSIĘWZIĘCIA (hub) — segment :id tras /power-projects/…
 *  wskazuje przedsięwzięcie, nie projekt ZE (connection). */
export function WpwMassLinks({
  connectionId,
  projectId,
  collapsible = false,
}: {
  connectionId: string;
  projectId: string;
  /** Zwijana karta, domyślnie zwinięta (zakładka WPW). */
  collapsible?: boolean;
}) {
  const { data: handovers } = useWpwHandovers({ connection: connectionId });
  const wpwOrders = useMemo(() => {
    const byId = new Map<
      string,
      {
        id: string;
        number: string;
        type: string;
        contractor: string | null;
        status: string;
        owners: number;
        returned: number; // przekazania z JAKIMKOLWIEK zwrotem z terenu (status inny niż „nie rozpoczęto")
      }
    >();
    for (const h of handovers ?? []) {
      if (!h.order) continue; // WPW poza zamówieniem — nie ma czego linkować
      let row = byId.get(h.order.id);
      if (!row) {
        row = {
          id: h.order.id,
          number: h.order.order_number ?? h.order.id.slice(0, 8),
          type: h.order.order_type_display,
          contractor: h.order.contractor,
          status: h.order.status_display,
          owners: 0,
          returned: 0,
        };
        byId.set(h.order.id, row);
      }
      row.owners += 1;
      if (h.declared_status !== "not_started") row.returned += 1;
    }
    return [...byId.values()];
  }, [handovers]);

  if (wpwOrders.length === 0) return null;
  return (
    <SectionCard
      className="mb-3 border-[var(--color-border-strong)]"
      bodyClassName="p-0 overflow-x-auto"
      collapsible={collapsible}
      defaultCollapsed={collapsible}
      title={
        <span className="inline-flex items-center gap-2 text-[var(--color-text-primary)]">
          <Layers size={13} /> Masowe pakiety WPW (ENERGA)
          <span className="font-normal normal-case tracking-normal text-[var(--color-text-muted)]">
            · formularze, kompilacja, zwroty z terenu
          </span>
        </span>
      }
    >
      <table className="w-full text-xs">
        <thead>
          <tr>
            <Th className="w-44">Zamówienie</Th>
            <Th className="w-40">Rodzaj</Th>
            <Th>Wykonawca</Th>
            <Th className="w-44">Status zamówienia</Th>
            <Th className="w-40">Zwroty z terenu</Th>
            <Th className="w-40" />
          </tr>
        </thead>
        <tbody>
          {wpwOrders.map((order) => {
            const to = `/power-projects/${projectId}/wpw-masowe/${order.id}`;
            return (
              <tr key={order.id} className="border-t border-[var(--color-border-muted)]">
                <td className="px-3 py-2">
                  <Link to={to} className="font-mono font-bold text-[var(--color-primary)] hover:underline">
                    {order.number}
                  </Link>
                </td>
                <td className="px-3 py-2">{order.type}</td>
                <td className="px-3 py-2">
                  {order.contractor ?? <span className="text-[var(--color-text-muted)]">—</span>}
                </td>
                <td className="px-3 py-2">
                  <Badge color="gray">{order.status}</Badge>
                </td>
                <td
                  className="px-3 py-2 font-mono text-[var(--color-text-secondary)]"
                  title="Właściciele, dla których negocjator zgłosił już jakikolwiek zwrot z terenu / wszyscy w zamówieniu"
                >
                  {order.returned} / {order.owners} właścicieli
                </td>
                <td className="px-3 py-2 text-right">
                  <Link
                    to={to}
                    className="inline-flex items-center gap-1 border border-[var(--color-border-strong)] px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-wider hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
                    title={`Otwórz masową generację pakietów dla zamówienia ${order.number}`}
                  >
                    Otwórz pakiet <ChevronRight size={12} />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SectionCard>
  );
}
